import { requestGmbh } from "../providers/utils/graphqlHelper";
import { ExtendSessionDocument } from "../graphql/gmbh/schema/gmbh-types";
import type { ExtendSessionMutation } from "../graphql/gmbh/schema/gmbh-types";

class SessionService {
  private static instance: SessionService;
  private extensionTimeout: NodeJS.Timeout | null = null;
  private isExtending = false;
  private currentExpiresAt: string | null = null;

  private constructor() {
    // Initialize the service
    this.currentExpiresAt = null;
  }

  public static getInstance(): SessionService {
    if (!SessionService.instance) {
      SessionService.instance = new SessionService();
    }
    return SessionService.instance;
  }

  private async extendSession(): Promise<void> {
    if (this.isExtending) return;

    try {
      this.isExtending = true;
      const response = await requestGmbh<ExtendSessionMutation>(ExtendSessionDocument);

      const session = response.data.extendSession;
      if (session?.expiresAt) {
        this.currentExpiresAt = session.expiresAt;
        this.scheduleTokenExtension();
      }
    } catch (error) {
      console.error("Failed to extend session:", error);
    } finally {
      this.isExtending = false;
    }
  }

  private scheduleTokenExtension(): void {
    if (this.extensionTimeout) {
      clearTimeout(this.extensionTimeout);
    }

    if (!this.currentExpiresAt) return;
    if (new Date(this.currentExpiresAt) <= new Date()) return;

    const expirationTime = new Date(this.currentExpiresAt).getTime();
    if (isNaN(expirationTime)) return;

    // Extend the session 5 minutes before expiration
    const extensionBuffer = 5 * 60 * 1000; // 5 minutes in milliseconds
    const timeUntilExtension = Math.max(0, expirationTime - Date.now() - extensionBuffer);

    this.extensionTimeout = setTimeout(() => {
      this.extendSession();
    }, timeUntilExtension);
  }

  public startSessionExtension(expiresAt: string): void {
    this.currentExpiresAt = expiresAt;
    this.scheduleTokenExtension();
  }

  public stopSessionExtension(): void {
    if (this.extensionTimeout) {
      clearTimeout(this.extensionTimeout);
      this.extensionTimeout = null;
    }
    this.currentExpiresAt = new Date().toISOString();
  }

  public isExpired(): boolean | null {
    if (!this.currentExpiresAt) {
      return null;
    }
    return new Date(this.currentExpiresAt) <= new Date();
  }
}

export const sessionService = SessionService.getInstance();
